import React, { FC, Fragment, useState, useRef, useMemo, Suspense } from 'react';
import {
  Box,
  Heading,
  Separator,
  SeparatorType,
  Set,
  SetSpacing,
  SetDirection,
  Button,
  Spinner,
  SpinnerSize,
  useSideSheetState,
} from '@bechtel/gds';
import { CommodityDocumentSectionProps, CommodityDetailProps } from './types';
import { get } from 'lodash';
import { CommoditySection, CommoditySectionField } from '@bechtel/types';
import { FieldValue, SuspenseLoader, ActionContent, WorkPackage3dViewer } from '@bechtel/client-common';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import { DocumentSectionStyled, DocumentViewStyled } from './styled';
import { t, Trans } from '@lingui/macro';

const CommodityDocumentSection: FC<CommodityDocumentSectionProps> = ({
  isoDocument,
  workPackageTag,
  commodityTag,
  commodityName,
}) => {
  const { visible: show3dViewer, open: open3dViewer, close: close3dViewer } = useSideSheetState(false);
  const pdfDocumentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number>(25);

  const onPageLoadSuccess = (page) => {
    if (!(pdfDocumentRef && pdfDocumentRef.current)) {
      return;
    }
    const widthScale = (pdfDocumentRef ? pdfDocumentRef?.current?.clientWidth : 0) / page.originalWidth;
    const heightScale = (pdfDocumentRef ? pdfDocumentRef?.current?.clientHeight : 0) / page.originalHeight;
    const pageScale = Math.min(widthScale, heightScale);
    if (scale !== pageScale) {
      setScale(widthScale * 100 - 3);
    }
  };

  const file = useMemo(() => ({ url: isoDocument?.url, withCredentials: true }), [isoDocument?.url]);
  return (
    <>
      <Separator type={SeparatorType.HORIZONTAL} spacing="600" color="grey.300" />
      <Box mb={{ sm: '500' }}>
        <Heading color="grey.1000" weight="600" data-testid="commodity-section-document-header">
          <Trans>Documents</Trans>
        </Heading>
      </Box>
      <DocumentSectionStyled id="pdfDocument" ref={pdfDocumentRef}>
        <DocumentViewStyled>
          <Document file={file} loading={<Spinner size={SpinnerSize.LARGE}></Spinner>}>
            <Page
              pageNumber={1}
              renderAnnotationLayer={false}
              scale={scale / 100}
              key={1}
              onLoadSuccess={onPageLoadSuccess}
            />
          </Document>
        </DocumentViewStyled>
      </DocumentSectionStyled>
      <Box mt={{ sm: '600' }}>
        <Box mr={{ sm: '400' }} flexShrink={{ sm: '0' }}>
          <Button data-testid="commodity-section-document-three-d-model" onClick={open3dViewer}>
            <ActionContent icon="cube" label={t`View 3D Model`} />
          </Button>
        </Box>
        <Box mr={{ sm: '400' }} flexShrink={{ sm: '0' }}>
          <Button
            data-testid="commodity-section-iso-documents-button"
            disabled={false}
            onClick={() => {
              window.open(get(isoDocument, 'url'), '_blank');
            }}
          >
            <ActionContent icon="ruler-combined" label={t`View ISO`} />
          </Button>
        </Box>
      </Box>
      {show3dViewer && (
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          <WorkPackage3dViewer
            workPackageTag={workPackageTag}
            commodityName={commodityName}
            commodityTag={commodityTag}
            onClose={close3dViewer}
          />
        </Suspense>
      )}
    </>
  );
};
export const SpecificationDetailView: FC<CommodityDetailProps> = ({
  commoditySections,
  isoDocument,
  showDocuments = false,
  commodityName,
  commodityTag,
  workPackageTag,
}) => {
  const lastItem = commoditySections.length;
  return (
    <Box flexDirection={{ sm: 'column' }} p={{ sm: '500' }} data-testid="commodity-section-detail-container">
      {commoditySections.map((commoditySection: CommoditySection, index: number) => {
        return (
          <Fragment key={commoditySection.title}>
            <Box mb={{ sm: '500' }}>
              <Heading color="grey.1000" weight="600" data-testid={`commodity-section-detail-title-${index}`}>
                {commoditySection.title}
              </Heading>
            </Box>
            <Set spacing={SetSpacing.COMPACT} direction={SetDirection.COL}>
              {commoditySection.fields.map((commoditySectionField: CommoditySectionField) => {
                return (
                  <Fragment key={commoditySectionField.name}>
                    <FieldValue field={commoditySectionField.name} value={commoditySectionField.value} />
                  </Fragment>
                );
              })}
            </Set>
            {index === 0 && showDocuments && isoDocument && (
              <CommodityDocumentSection
                workPackageTag={workPackageTag}
                commodityName={commodityName}
                commodityTag={commodityTag}
                isoDocument={isoDocument}
              />
            )}
            {lastItem !== index + 1 && <Separator type={SeparatorType.HORIZONTAL} spacing="600" color="grey.300" />}
          </Fragment>
        );
      })}
    </Box>
  );
};
